import { cn } from "@/utils/cn";
import type { Selection, StageId } from "./model";
import { DOMAIN_MAX, DOMAINS, EXCLUDE_PRESETS, STAGES } from "./model";

export function ConditionStep({
  selection,
  onStage,
  onToggleDomain,
  onToggleExclude,
}: {
  selection: Selection;
  onStage: (id: StageId) => void;
  onToggleDomain: (id: string) => void;
  onToggleExclude: (id: string) => void;
}) {
  const domainSet = new Set(selection.domainIds);
  const excludeSet = new Set(selection.excludeIds);
  const domainFull = selection.domainIds.length >= DOMAIN_MAX;

  return (
    <div className="trial-fade-in space-y-5">
      <div>
        <h3 className="mb-1 text-[17px] font-bold tracking-tight text-[var(--trial-foreground)]">조건을 좁혀볼까요?</h3>
        <p className="text-[13px] text-[var(--trial-muted)]">모두 선택 사항이에요. 비워두면 더 넓게 발굴합니다.</p>
      </div>

      <div>
        <div className="mb-2 text-[12.5px] font-semibold uppercase tracking-wide text-[var(--trial-muted)]">선호 회사 단계</div>
        <div className="flex flex-wrap gap-2">
          {STAGES.map((stage) => {
            const active = selection.stage === stage.id;
            return (
              <button
                key={stage.id}
                type="button"
                onClick={() => onStage(stage.id)}
                className={cn(
                  "rounded-full px-3.5 py-2 text-[13px] font-medium transition-all hover:-translate-y-0.5",
                  active
                    ? "trial-brand-gradient text-white shadow-[0_4px_14px_-3px_rgba(79,109,245,0.5)]"
                    : "trial-glass-subtle text-[var(--trial-muted-strong)] hover:text-[var(--trial-foreground)]",
                )}
                title={stage.desc}
              >
                {stage.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-[12.5px] font-semibold uppercase tracking-wide text-[var(--trial-muted)]">도메인·산업</span>
          <span className="text-[11.5px] text-[var(--trial-muted)]">최대 {DOMAIN_MAX}개</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {DOMAINS.map((domain) => {
            const active = domainSet.has(domain.id);
            const disabled = !active && domainFull;
            return (
              <button
                key={domain.id}
                type="button"
                onClick={() => onToggleDomain(domain.id)}
                disabled={disabled}
                className={cn(
                  "rounded-full px-3.5 py-2 text-[13px] font-medium transition-all",
                  active
                    ? "bg-[var(--trial-brand-bg)] text-[var(--trial-brand)] ring-1 ring-[var(--trial-brand-soft)] hover:-translate-y-0.5"
                    : disabled
                      ? "trial-glass-subtle cursor-not-allowed text-[var(--trial-muted)]/40"
                      : "trial-glass-subtle text-[var(--trial-muted-strong)] hover:-translate-y-0.5 hover:text-[var(--trial-foreground)]",
                )}
              >
                {!active && <span className="mr-0.5 opacity-50">+</span>}
                {domain.label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="mb-2 text-[12.5px] font-semibold uppercase tracking-wide text-[var(--trial-muted)]">이런 후보는 제외</div>
        <div className="space-y-2">
          {EXCLUDE_PRESETS.map((exclude) => {
            const active = excludeSet.has(exclude.id);
            return (
              <button
                key={exclude.id}
                type="button"
                onClick={() => onToggleExclude(exclude.id)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-xl px-3.5 py-2.5 text-left transition-all",
                  active ? "bg-[#FEF2F2] ring-1 ring-[#FECACA]" : "trial-glass-subtle hover:bg-white/60",
                )}
              >
                <span
                  className={cn(
                    "flex h-5 w-5 shrink-0 items-center justify-center rounded-md transition-colors",
                    active
                      ? "bg-[var(--trial-danger)] text-white"
                      : "border border-[var(--trial-border-strong)] text-transparent",
                  )}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span className={cn("text-[13.5px]", active ? "font-medium text-[#991B1B]" : "text-[var(--trial-muted-strong)]")}>
                  {exclude.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
